// Hand Advisor — a helper for playing at a real table. Tap in the tiles you
// are holding; the app ranks the 2026 card hands you are closest to, shows
// what to keep and what you still need, and suggests a discard (or three
// Charleston passes). State lives in-module and is mirrored to localStorage
// so a locked phone doesn't lose the hand mid-game.

import { renderTile } from '../ui/render.js';
import { tileFromKey, tileKey, tileName, sortHand } from '../engine/tiles.js';
import { SUITS, SUIT_NAMES, WINDS, DRAGONS } from '../engine/constants.js';
import { adviseHand, explainDiscard, COPIES, COPIES_DEFAULT } from '../engine/advisor.js';

const STORAGE_KEY = 'mahjong.advisor';
const MAX_TILES = 14;

let nextId = 1;
let hand = [];              // tiles, each with a unique id
let hasExposures = false;
let mode = 'play';          // 'play' | 'charleston'
let onBack = null;

// ── persistence ─────────────────────────────────────────────────────────────
function save() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      keys: hand.map(tileKey), hasExposures, mode,
    }));
  } catch { /* private mode etc. — advisor still works, just won't persist */ }
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const s = JSON.parse(raw);
    hand = (s.keys || []).map((k) => tileFromKey(k, nextId++)).filter(Boolean).slice(0, MAX_TILES);
    hasExposures = !!s.hasExposures;
    mode = s.mode === 'charleston' ? 'charleston' : 'play';
  } catch { hand = []; }
}

// ── keypad ──────────────────────────────────────────────────────────────────
const KEYPAD_ROWS = [
  ...SUITS.map((s) => ({ label: SUIT_NAMES[s] + 's', keys: [1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => s + n) })),
  {
    label: 'Winds · Dragons · Flower · Joker',
    keys: [...WINDS.map((w) => 'wind_' + w), ...DRAGONS.map((d) => 'dragon_' + d), 'flower', 'joker'],
  },
];

function heldCount(key) {
  let n = 0;
  for (const t of hand) if (tileKey(t) === key) n++;
  return n;
}

function buildKeypad() {
  let h = '';
  for (const row of KEYPAD_ROWS) {
    h += `<div class="adv-key-label">${row.label}</div><div class="adv-key-row">`;
    for (const k of row.keys) {
      const t = tileFromKey(k);
      h += `<button class="adv-key" data-key="${k}" title="Add ${tileName(t)}">` +
        renderTile(t, '', true) + `<span class="adv-key-count" data-count="${k}"></span></button>`;
    }
    h += '</div>';
  }
  document.getElementById('advKeypad').innerHTML = h;
}

function refreshKeypadCounts() {
  document.querySelectorAll('#advKeypad .adv-key').forEach((btn) => {
    const k = btn.dataset.key;
    const n = heldCount(k);
    const max = COPIES[k] || COPIES_DEFAULT;
    const badge = btn.querySelector('.adv-key-count');
    badge.textContent = n ? String(n) : '';
    badge.style.display = n ? '' : 'none';
    btn.disabled = n >= max || hand.length >= MAX_TILES;
  });
}

// ── hand row ────────────────────────────────────────────────────────────────
function addTile(key) {
  if (hand.length >= MAX_TILES) return;
  const max = COPIES[key] || COPIES_DEFAULT;
  if (heldCount(key) >= max) return;
  hand.push(tileFromKey(key, nextId++));
  sortHand(hand);
  update();
}

function removeTile(id) {
  hand = hand.filter((t) => t.id !== id);
  update();
}

function clearHand() {
  hand = [];
  update();
}

function tileHtml(t, cls = '') {
  return `<div class="adv-hand-tile" data-id="${t.id}" title="Tap to remove ${tileName(t)}">${renderTile(t, cls, false)}</div>`;
}

// ── results ─────────────────────────────────────────────────────────────────
function renderResults(advice) {
  const el = document.getElementById('advResults');
  const n = hand.length;
  if (n < 5) {
    el.innerHTML = `<div class="adv-empty">Tap the tiles you are holding — all of them, including any sets you have exposed. Suggestions appear once you have entered a few tiles.</div>`;
    return;
  }

  let h = '';

  if (advice.win) {
    h += `<div class="adv-card adv-win"><div class="adv-card-title">🎉 That's Mahjong!</div>` +
      `<div>Your 14 tiles complete <b>${advice.win.hand.label}</b> (${advice.win.hand.desc}) — worth ${advice.win.value}.</div></div>`;
  }

  // Suggestion box: one discard (play) or three passes (charleston).
  const top = advice.targets[0];
  if (mode === 'charleston') {
    const pass = advice.charlestonPass;
    h += `<div class="adv-card adv-suggest"><div class="adv-card-title">🔁 Pass these ${pass.length}</div>` +
      `<div class="adv-tile-row">${pass.map((t) => renderTile(t, 'adv-toss', false)).join('')}</div>` +
      `<div class="adv-note">The tiles that help your closest hands the least. Jokers are never passed.</div></div>`;
  } else if (advice.discard) {
    const soft = n < 14 ? ' (when you have 14)' : '';
    h += `<div class="adv-card adv-suggest"><div class="adv-card-title">🗑️ Discard${soft}</div>` +
      `<div class="adv-suggest-row">${renderTile(advice.discard, 'adv-toss', false)}` +
      `<div><b>${tileName(advice.discard)}</b><div class="adv-note">${explainDiscard(advice.discard, advice.targets)}</div></div></div>`;
    const alts = advice.discardOrder.slice(1, 4).filter((t) => t.type !== 'joker');
    if (alts.length) {
      h += `<div class="adv-note" style="margin-top:8px">Next best to let go: ${alts.map(tileName).join(', ')}</div>`;
    }
    h += '</div>';
  }

  // Ranked hands.
  h += `<div class="adv-section-head">Closest hands on the card</div>`;
  advice.targets.forEach((t) => {
    const pct = Math.round((t.score / 14) * 100);
    const need = t.missing.length
      ? t.missing.map((m) => {
          const dead = m.left < m.count && !m.jokerOk;
          return `<span class="adv-need${dead ? ' dead' : ''}" title="${m.left} left in the set${m.jokerOk ? ' · jokers OK' : ' · no jokers'}">${m.count}× ${m.name}</span>`;
        }).join('')
      : '<span class="adv-need done">complete!</span>';
    const jokerNote = t.spareJokers ? ` · ${t.spareJokers} joker${t.spareJokers > 1 ? 's' : ''} to spare` : '';
    h += `<div class="adv-card adv-target${t === top ? ' best' : ''}">` +
      `<div class="adv-target-head"><span class="adv-rank">#${t.rank}</span>` +
      `<span class="adv-target-label">${t.label}</span>` +
      `<span class="adv-fit"><span class="adv-fit-bar"><i style="width:${pct}%"></i></span>${t.score}/14</span></div>` +
      `<div class="adv-target-desc">${t.desc || ''} · ${t.section} · ${t.value} pts${t.concealed ? ' · Concealed' : ''}${jokerNote}</div>` +
      `<div class="adv-tile-row">${t.keep.map((x) => renderTile(x, 'adv-keep', true)).join('')}` +
      `${t.toss.map((x) => renderTile(x, 'adv-toss', true)).join('')}</div>` +
      `<div class="adv-needs"><span class="adv-needs-label">Still need:</span>${need}</div>` +
      `</div>`;
  });

  el.innerHTML = h;
}

function update() {
  save();
  const count = document.getElementById('advCount');
  count.textContent = `${hand.length} / ${MAX_TILES}`;
  count.classList.toggle('full', hand.length >= MAX_TILES);

  const advice = adviseHand(hand, { hasExposures, top: 5 });
  const tossIds = new Set();
  if (hand.length >= 5 && advice.targets[0]) {
    if (mode === 'charleston') advice.charlestonPass.forEach((t) => tossIds.add(t.id));
    else if (advice.discard) tossIds.add(advice.discard.id);
  }
  document.getElementById('advHand').innerHTML = hand.length
    ? hand.map((t) => tileHtml(t, tossIds.has(t.id) ? 'adv-toss' : '')).join('')
    : '<div class="adv-hand-empty">No tiles yet — tap below to add them.</div>';

  refreshKeypadCounts();
  renderResults(advice);
}

// ── wiring ──────────────────────────────────────────────────────────────────
let wired = false;

export function showAdvisor(back) {
  onBack = back;
  if (!wired) wire();
  update();
}

function wire() {
  wired = true;
  load();
  buildKeypad();

  document.getElementById('advKeypad').addEventListener('click', (e) => {
    const btn = e.target.closest('.adv-key');
    if (btn && !btn.disabled) addTile(btn.dataset.key);
  });
  document.getElementById('advHand').addEventListener('click', (e) => {
    const el = e.target.closest('.adv-hand-tile');
    if (el) removeTile(Number(el.dataset.id));
  });
  document.getElementById('btnAdvClear').addEventListener('click', clearHand);
  document.getElementById('btnAdvBack').addEventListener('click', () => onBack && onBack());

  const exp = document.getElementById('advExposed');
  exp.checked = hasExposures;
  exp.addEventListener('change', () => { hasExposures = exp.checked; update(); });

  document.querySelectorAll('.adv-mode').forEach((btn) => {
    btn.classList.toggle('sel', btn.dataset.mode === mode);
    btn.addEventListener('click', () => {
      mode = btn.dataset.mode;
      document.querySelectorAll('.adv-mode').forEach((b) => b.classList.toggle('sel', b === btn));
      update();
    });
  });
}
